import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty, isNilOrEmptyOrZero } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { formPaths } from '@shared/constants/form-paths.const';
import { getSchedaPrevalenzaControl, getSchedaPrevalenzaGroup } from './helpers/natura-mutualistica-form-validators.helper';

export const naturaMutualisticaBilancioRules: FormGroupRules = {
    my_NumBilanciInExcel: (form: FormGroup) => {
        const datiBilancioGroup = getGroup(form, `${formPaths.datiBilancio}`);
        const my_NumBilanciInExcelControl = getControl(form, `${formPaths.bilancio}.my_NumBilanciInExcel`);
        const my_NumBilanciInExcel: string = my_NumBilanciInExcelControl?.value;
        const my_numBilanciNelDBCentrale = toNum(getControl(form, 'my_numBilanciNelDBCentrale')?.value);
        const my_TipoBilancioAbbr1: string = getControl(datiBilancioGroup, 'my_TipoBilancioAbbr1')?.value;
        const my_DataApprovazione1: string = getControl(datiBilancioGroup, 'my_DataApprovazione1')?.value;
        const my_TipoSchemaBil1: string = getControl(datiBilancioGroup, 'my_TipoSchemaBil1')?.value;
        const my_BilAnno2: string = getControl(datiBilancioGroup, 'my_BilAnno2')?.value;
        const errors = my_NumBilanciInExcelControl.errors ? my_NumBilanciInExcelControl.errors : {};
        const informazioniPrimoBilancioStatus = 'Attenzione: nel file ci sono informazioni per il primo bilancio';

        addRemoveError(isNilOrEmpty(my_NumBilanciInExcel), errors, 'my_NumBilanciInExcel', {
            status: 'Attenzione: indicare il numero di bilanci da inserire',
        });
        addRemoveError(my_NumBilanciInExcel === '0' && my_numBilanciNelDBCentrale >= 1, errors, 'my_NumBilanciInExcel', {
            status: 'Attenzione: dal sistema centrale risulta depositato almeno 1 bilancio',
        });
        addRemoveError(my_NumBilanciInExcel === '1' && my_numBilanciNelDBCentrale >= 2, errors, 'my_NumBilanciInExcel', {
            status: 'Attenzione: dal sistema centrale risultano depositati almeno 2 bilanci',
        });
        addRemoveError(my_NumBilanciInExcel === '1' && !isNilOrEmptyOrZero(toNum(my_TipoBilancioAbbr1)), errors, 'my_NumBilanciInExcel', {
            status: informazioniPrimoBilancioStatus,
        });
        addRemoveError(my_NumBilanciInExcel === '1' && !isNilOrEmpty(my_DataApprovazione1), errors, 'my_NumBilanciInExcel', {
            status: informazioniPrimoBilancioStatus,
        });
        addRemoveError(
            my_NumBilanciInExcel === '2' && isNilOrEmpty(my_TipoSchemaBil1) && isNilOrEmpty(my_DataApprovazione1) && isNilOrEmpty(my_TipoBilancioAbbr1),
            errors,
            'my_NumBilanciInExcel',
            { status: 'Attenzione: manca il bilancio del primo anno' }
        );
        addRemoveError(toNum(my_NumBilanciInExcel) !== 0 && my_BilAnno2 === '1900', errors, 'my_NumBilanciInExcel', {
            status: 'Attenzione: anno del bilancio incoerente',
        });

        my_NumBilanciInExcelControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_lblScheda: (form: FormGroup) => {
        const schedaPrevalenzaGroup = getSchedaPrevalenzaGroup(form);
        const my_lblSchedaControl = getControl(schedaPrevalenzaGroup, 'my_lblScheda');
        const errors = my_lblSchedaControl.errors ? my_lblSchedaControl.errors : {};

        const getValue = (fieldName: string, useSuffix = false): string => {
            const suffix = useSuffix ? 'PrimoAnno' : '';
            return getControl(schedaPrevalenzaGroup, fieldName + suffix)?.value;
        };
        const isFilled = (v: string | null | undefined) => !isNilOrEmpty(v);
        const hasAtLeastOneCompletePair = (pairs: string[][]) => pairs.some(([a, b]) => isFilled(a) && isFilled(b));

        const pairSecondoAnno = [
            [getValue('my_A1versoSoci'), getValue('my_TotaleA1')],
            [getValue('my_B9versoSoci'), getValue('my_TotaleB9')],
            [getValue('my_B6confSoci'), getValue('my_TotaleB6')],
            [getValue('my_QtaConfSoci'), getValue('my_TotQtaConfSoci')],
            [getValue('my_B7ricevSoci'), getValue('my_TotaleB7')],
        ];
        const pairPrimoAnno = [
            [getValue('my_A1versoSoci', true), getValue('my_TotaleA1', true)],
            [getValue('my_B9versoSoci', true), getValue('my_TotaleB9', true)],
            [getValue('my_B6confSoci', true), getValue('my_TotaleB6', true)],
            [getValue('my_QtaConfSoci', true), getValue('my_TotQtaConfSoci', true)],
            [getValue('my_B7ricevSoci', true), getValue('my_TotaleB7', true)],
        ];

        const scSocObbl: string = getControl(form, `${formPaths.schedaCoopSociali}.my_ScSocObbl`)?.value;
        const dataApp1: string = getControl(form, `${formPaths.datiBilancio}.my_DataApprovazione1`)?.value;
        const tipoSchemaBil1: string = getControl(form, `${formPaths.datiBilancio}.my_TipoSchemaBil1`)?.value;
        const bilancioAllegato: string = getControl(form, `${formPaths.bilancio}.my_BilancioAllegato`)?.value;

        const isObbligoPrevalenza = scSocObbl === '0';
        const prereqPrimoAnnoOk = !isNilOrEmpty(dataApp1) && !isNilOrEmpty(tipoSchemaBil1) && !isNilOrEmpty(bilancioAllegato);
        // manifest.xsf definisce due errorCondition indipendenti su questo campo (2° anno e 1° anno), ciascuna
        // col proprio messaggio. Riusare lo stesso `status` per entrambe le addRemoveError fa sì che, quando è
        // vera solo una delle due, la seconda chiamata (condizione falsa) cancelli l'errore appena impostato
        // dalla prima — perché il confronto per l'eliminazione avviene sul corpo dell'errore.
        const statusSecondoAnno = 'Calcolare la percentuale di prevalenza per almeno uno dei punti 1, 2, 3 per il 2° anno';
        const statusPrimoAnno = 'Calcolare la percentuale di prevalenza per almeno uno dei punti 1, 2, 3 per il 1° anno';

        addRemoveError(isObbligoPrevalenza && !hasAtLeastOneCompletePair(pairSecondoAnno), errors, 'my_lblScheda', { status: statusSecondoAnno });
        addRemoveError(isObbligoPrevalenza && !hasAtLeastOneCompletePair(pairPrimoAnno) && prereqPrimoAnnoOk, errors, 'my_lblScheda', { status: statusPrimoAnno });

        my_lblSchedaControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};
